import React, { useState, useRef, useEffect } from 'react';
import {
  TextField, Button, InputLabel, MenuItem, FormControl, Select, Box, FormHelperText, Autocomplete, InputAdornment, IconButton
} from '@mui/material';
import { Formik, useFormik } from 'formik';
import * as yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Country, State, City } from 'country-state-city';
import { checkCompanyEmail } from '../../../redux/authSlice';

const nonEngChar = /[^-A-Za-z0-9_@,.:;{}/&%()'"#*<>!$?+\n ]/;
const allDigitsSame = value => /^(.)\1+$/.test(value);
export const CONTACT_DETAILS_VALIDATIONS = (dispatch) => yup.object().shape({
  emailAddress: yup.string().required('Email cannot be empty.')
    .test('checkDuplication', 'Email already exists', async (value) => {
      try {
        // console.log('VALUES::', value);
        const email = value
        const response = await dispatch(checkCompanyEmail(email));
        if (response.payload[0].exists !== 'No') { // Assuming true means exists
          // console.log('response payload::', response.payload[0].exists)
          return false; // Validation fails
        }
        // console.log('HEAR');
        return true; // Validation passes
      } catch (error) {
        return false; // Validation fails on error
      }
    })
    .matches(
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,6}$/i,
      'Invalid email format. Example: example@example.com'
    )
    .max(255, 'Email must be at most 255 characters long.'),
  uIsdCode: yup.string().required('ISD code cannot be empty.'),
  phoneNumber: yup.string().required('Phone number is required.')
    .matches(/^[0-9]+$/, 'Phone number must only contain digits.')
    .test('notAllDigitsSame', 'All digits cannot be the same.', value => !allDigitsSame(value)),
  uAdditionalPhone: yup.string()
    // .required('Additional phone number is required.')
    .matches(/^[0-9]+$/, 'Additional phone number must only contain digits.')
    .test('notAllDigitsSame', 'All digits cannot be the same.', value => !allDigitsSame(value))
    .notOneOf([yup.ref('phoneNumber')], 'Additional phone number must be different from the main phone number.')
    .nullable(true)
    .default(''),
  companyGst: yup.string()
    .required('GST/VAT/Tax ID cannot be empty')
    .matches(/^[a-zA-Z0-9]+$/, 'Invalid format. Only alphanumeric characters allowed.')
    .length(15, 'GST/VAT/Tax ID must be exactly 15 characters long.'),
})

const ContactDetails = ({ onSubmit, handleStepChange, currentStep }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const initialState = {
    uIsdCode: '',
    phoneNumber: '',
    uAdditionalPhone: '',
    emailAddress: '',
    companyGst: '',
  }
  const { loading } = useSelector((state) => state.auth);
  const [countries, setCountries] = useState([]);
  const [selectedISD, setSelectedISD] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);

  useEffect(() => {
    const fetchCountries = async () => {
      const countriesWithISD = Country.getAllCountries().map(country => ({
        code: country.phonecode,
        name: country.name
      }));
      setCountries(countriesWithISD);
    };
    fetchCountries();
  }, []);


  const handleChange = (_, value) => {
    if (value) {
      const numericISD = value.code.replace(/[^\d]/g, ''); // Extract numeric part from ISD code
      setSelectedISD(parseInt(numericISD)); // Convert to number
    } else {
      setSelectedISD(''); // Reset ISD code if no value selected
    }
  };
  return (
    <Formik

      initialValues={initialState}
      enableReinitialize={true}
      // validationSchema={CONTACT_DETAILS_VALIDATIONS(dispatch)}
      onSubmit={(values, { setSubmitting }) => {
        const formattedValues = {
          ...values,
        };
        // console.log('Formatted Submit:', formattedValues);
        // console.log(values, "value before ")
        onSubmit(formattedValues);

        setSubmitting(false);
      }}
    >

      {({ values, errors, handleChange, handleBlur, handleSubmit, setFieldValue, touched }) => (
        <form onSubmit={handleSubmit}>
          {/* <Box sx={{ display: 'flex', gap: 2 }}></Box> */}
          <div className="row">
            {/* <Box sx={{ display: 'flex', gap: 2 }}> */}
            <div className="col-md-2 cs-form mb-3">
              <FormControl className='md-input'>
                <InputLabel id="unit-label"></InputLabel>
                <Autocomplete
                  id="country-select"
                  options={countries}
                  value={countries.find(country => country.code === selectedISD)}
                  onChange={(event, newValue) => {
                    setSelectedCountry(newValue);
                    setFieldValue('uIsdCode', newValue ? newValue.code : '');
                  }}
                  getOptionLabel={(option) => option.code}
                  renderInput={(params) => <TextField {...params} label="ISD *" variant="outlined" />}
                />
                <FormHelperText>{touched.uIsdCode && errors.uIsdCode}</FormHelperText>
              </FormControl>


            </div>
            <div className="col-md-5 cs-form mb-3">
              <TextField
                label='Phone *'
                className='md-input'
                name='phoneNumber'
                value={values.phoneNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.phoneNumber}
                helperText={errors.phoneNumber}
              />

            </div>

            <div className="col-md-5 cs-form mb-3">
              <TextField
                label='Additional Phone'
                className='md-input'
                name='uAdditionalPhone'
                value={values.uAdditionalPhone}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.uAdditionalPhone}
                helperText={errors.uAdditionalPhone}
              />

            </div>



            <div className="col-md-7 cs-form mb-3">
              <TextField
                label='Email *'
                className='md-input'
                name='emailAddress'
                value={values.emailAddress}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.emailAddress}
                helperText={errors.emailAddress}
              />
            </div>
            <div className="col-md-5 cs-form mb-3">
              <TextField
                label='GST/VAT/Tax id *'
                className='md-input'
                name='companyGst'
                value={values.companyGst}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.companyGst}
                helperText={errors.companyGst}
              />
            </div>

            <div className="d-flex justify-content-between mt-3">
              <button type="button" className="btn btn-light grey-btn lg-btn"
                onClick={() => handleStepChange(currentStep - 1)}>
                Back
              </button>
              <div>


                <button type="submit" className="btn btn-primary greeen-btn lg-btn lg-btn"
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}>
                  {loading ? 'Submitting...' : 'Next'}

                </button>
              </div>
            </div>
          </div>
        </form>
      )}
    </Formik>
  )
}




export default ContactDetails